package com.evoapps.evoshift.data.repository

import com.evoapps.evoshift.domain.model.NotFoundException
import com.evoapps.evoshift.data.dto.roster.AvailabilityDto
import com.evoapps.evoshift.data.dto.roster.AvailabilityPeriodDto
import com.evoapps.evoshift.data.dto.roster.AvailabilityRequestDto
import com.evoapps.evoshift.data.dto.roster.AvailabilitySharingDto
import com.evoapps.evoshift.data.dto.roster.SharedAvailabilityDto
import com.evoapps.evoshift.domain.model.AvailabilitySharing
import com.evoapps.evoshift.domain.model.SharedAvailability
import com.evoapps.evoshift.data.dto.roster.WeeklyAvailabilityTemplateDto
import com.evoapps.evoshift.data.mapper.AvailabilityMapper
import com.evoapps.evoshift.domain.model.Availability
import com.evoapps.evoshift.domain.model.AvailabilityRequest
import com.evoapps.evoshift.domain.model.AvailabilityEmailLinks
import com.evoapps.evoshift.domain.model.AvailabilityStatus
import com.evoapps.evoshift.domain.model.DayPreference
import com.evoapps.evoshift.domain.model.WeeklyAvailabilityTemplate
import com.evoapps.evoshift.domain.repository.AvailabilityRepository
import com.evoapps.evoshift.data.remote.AppFirestore
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDate
import kotlinx.datetime.minus
import com.evoapps.evoshift.domain.model.AvailabilityPeriod

class AvailabilityRepositoryImpl(
    private val firestore: AppFirestore,
    private val availabilityMapper: AvailabilityMapper
) : AvailabilityRepository {

    override suspend fun getAvailability(
        userId: String,
        workplaceId: String,
        month: String
    ): Result<Availability> {
        return try {
            val docId = "${userId}_${workplaceId}_$month"
            val snapshot = firestore.collection("availability").document(docId).get()

            if (snapshot.exists) {
                val dto = snapshot.data<AvailabilityDto>()
                Result.success(availabilityMapper.toDomain(dto))
            } else {
                // Parse month ISO "YYYY-MM"
                val parts = month.split("-")
                val year = parts[0].toInt()
                val monthInt = parts[1].toInt()

                // Determine days in month
                val firstDayOfNextMonth = if (monthInt == 12) {
                    LocalDate(year + 1, 1, 1)
                } else {
                    LocalDate(year, monthInt + 1, 1)
                }
                val lastDay = firstDayOfNextMonth.minus(DatePeriod(days = 1))

                val defaultDays = (1..lastDay.day).associate { day ->
                    day to DayPreference(AvailabilityStatus.AVAILABLE, null, null)
                }

                val defaultAvailability = Availability(
                    userId = userId,
                    rosterId = null,
                    workplaceId = workplaceId,
                    month = month,
                    isLocked = false,
                    isFilled = false,
                    days = defaultDays
                )
                Result.success(defaultAvailability)
            }
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun saveAvailability(availability: Availability): Result<Unit> {
        return try {
            val wpId = availability.workplaceId ?: "unknownWorkplace"
            val docId = "${availability.userId}_${wpId}_${availability.month}"
            val dto = availabilityMapper.toDto(availability)
            firestore.collection("availability").document(docId).set(dto)
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    private fun copyId(userId: String, workplaceId: String, month: String) = "${userId}_${workplaceId}_$month"

    override suspend fun findAvailability(userId: String, workplaceId: String, month: String): Result<Availability?> = runCatching {
        val snapshot = firestore.collection("availability").document(copyId(userId, workplaceId, month)).get()
        if (snapshot.exists) availabilityMapper.toDomain(snapshot.data<AvailabilityDto>()) else null
    }

    override suspend fun deleteAvailability(userId: String, workplaceId: String, month: String): Result<Unit> = runCatching {
        firestore.collection("availability").document(copyId(userId, workplaceId, month)).delete()
    }

    private fun sharedMonths(userId: String) = firestore.collection("users").document(userId).collection("availability")

    override suspend fun getSharedAvailability(userId: String, month: String): Result<SharedAvailability?> = runCatching {
        val snapshot = sharedMonths(userId).document(month).get()
        if (snapshot.exists) availabilityMapper.toDomain(snapshot.data<SharedAvailabilityDto>()) else null
    }

    override suspend fun getSharedAvailabilityFrom(userId: String, fromMonth: String): Result<List<SharedAvailability>> = runCatching {
        sharedMonths(userId).where { "month" greaterThanOrEqualTo fromMonth }.get().documents
            .map { availabilityMapper.toDomain(it.data<SharedAvailabilityDto>()) }
            .sortedBy { it.month }
    }

    override suspend fun saveSharedAvailability(availability: SharedAvailability): Result<Unit> = runCatching {
        sharedMonths(availability.userId).document(availability.month).set(availabilityMapper.toDto(availability))
    }

    private fun sharingDoc(userId: String) =
        firestore.collection("users").document(userId).collection("settings").document("availability")

    override suspend fun getSharing(userId: String): Result<AvailabilitySharing> = runCatching {
        val snapshot = sharingDoc(userId).get()
        if (!snapshot.exists) AvailabilitySharing()
        else snapshot.data<AvailabilitySharingDto>().let { AvailabilitySharing(it.shareWithAllEmployers, it.updatedAt) }
    }

    override suspend fun saveSharing(userId: String, sharing: AvailabilitySharing): Result<Unit> = runCatching {
        sharingDoc(userId).set(AvailabilitySharingDto(sharing.shareWithAllEmployers, sharing.updatedAt))
    }

    override suspend fun createAvailabilityRequest(request: AvailabilityRequest): Result<Unit> {
        return try {
            val dto = AvailabilityRequestDto(
                id = request.id,
                workplaceId = request.workplaceId,
                rosterId = request.rosterId,
                month = request.month,
                period = AvailabilityPeriodDto(request.period.startDate, request.period.endDate),
                dueDate = request.dueDate,
                sentAt = request.sentAt,
                createdBy = request.createdBy,
                message = request.message,
                emailLinks = request.emailLinks.stored
            )
            firestore.collection("availability_requests").document(request.id).set(dto)
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun getAvailabilityRequest(requestId: String): Result<AvailabilityRequest> {
        return try {
            val snapshot = firestore.collection("availability_requests").document(requestId).get()
            if (snapshot.exists) {
                val dto = snapshot.data<AvailabilityRequestDto>()
                val request = AvailabilityRequest(
                    id = dto.id,
                    workplaceId = dto.workplaceId,
                    rosterId = dto.rosterId,
                    month = dto.month,
                    period = AvailabilityPeriod(
                        dto.period.startDate,
                        dto.period.endDate
                    ),
                    dueDate = dto.dueDate,
                    sentAt = dto.sentAt,
                    createdBy = dto.createdBy,
                    message = dto.message,
                    emailLinks = AvailabilityEmailLinks.fromStored(dto.emailLinks)
                )
                Result.success(request)
            } else {
                Result.failure(NotFoundException("Availability request $requestId"))
            }
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun getAvailabilityForRoster(rosterId: String): Result<List<Availability>> {
        return try {
            // Query availability documents where rosterId matches
            val snapshot = firestore.collection("availability")
                .where { "rosterId" equalTo rosterId }
                .get()

            val list = snapshot.documents.map {
                availabilityMapper.toDomain(it.data<AvailabilityDto>())
            }
            Result.success(list)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun getWeeklyAvailabilityTemplates(
        userId: String,
        workplaceId: String
    ): Result<List<WeeklyAvailabilityTemplate>> {
        return try {
            val snapshot = firestore.collection("weekly_availability_templates")
                .where { "userId" equalTo userId }
                .where { "workplaceId" equalTo workplaceId }
                .get()

            val list = snapshot.documents.map {
                availabilityMapper.toDomainTemplate(it.data<WeeklyAvailabilityTemplateDto>())
            }
            Result.success(list)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun saveWeeklyAvailabilityTemplate(
        template: WeeklyAvailabilityTemplate
    ): Result<Unit> {
        return try {
            val docId =
                if (template.id.isBlank()) firestore.collection("weekly_availability_templates").document.id else template.id
            val finalTemplate = if (template.id.isBlank()) template.copy(id = docId) else template
            val dto = availabilityMapper.toDtoTemplate(finalTemplate)
            firestore.collection("weekly_availability_templates").document(docId).set(dto)
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun deleteWeeklyAvailabilityTemplate(templateId: String): Result<Unit> {
        return try {
            firestore.collection("weekly_availability_templates").document(templateId).delete()
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }
}
